// services/coverPickAhead.js
//
// The look a new invitation opens with on the website - a random cover
// template, sometimes with a video theme - fetched before Create is opened, so
// it shows instantly and is different every time. The choosing is
// shared/coverPickAhead.js (the app does the same with files on the device).
//
// ── The cycle ──────────────────────────────────────────────────────────────
//
//   Site loads     startCoverPickAhead(): if no look is ready, pick one and
//                  fetch its files in the background.
//   Create opens   takeCoverPick(): hands the ready look over and starts
//                  fetching the next one.
//
// Fetched into the browser's own HTTP cache: cdn.invits.co serves templates
// and themes with a year's max-age, so the <img> and <video> on the create
// page load them from disk under their ordinary CDN addresses. Nothing else
// changes - what is saved, what a draft keeps - and the browser, not this
// file, decides when old files leave its cache. Only the choice itself is
// kept, in localStorage.
//
// Videos are left out when the visitor has asked to save data or is on a
// slow connection. Everything here is best effort: when nothing is ready,
// Create falls back to its bundled backgrounds.

import supabase from './supabase';
import {
  choosePick,
  TEMPLATE_POOL_COLUMNS,
  THEME_POOL_COLUMNS,
  TEMPLATE_POOL_LIMIT,
  THEME_POOL_LIMIT,
} from '../shared/coverPickAhead';

const STORE_KEY = 'invits.coverPickAhead.v1';
/** After a look is taken, wait this long before fetching the next - Create is busy opening. */
const NEXT_DELAY_MS = 4000;

let preparing = null;
/** When the current look was taken, so a second take moments later gets the same one. */
let takenAt = 0;

const read = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
    return { ready: saved?.ready || null, current: saved?.current || null };
  } catch {
    return { ready: null, current: null };
  }
};

const write = (state) => {
  try {
    localStorage.setItem(STORE_KEY, JSON.stringify(state));
  } catch {
    // Private windows and full storage: the page falls back, nothing breaks.
  }
};

/** Whether a video (a few MB) is worth fetching on this connection. */
const videoAllowed = () => {
  const c = navigator.connection;
  if (!c) return true;
  if (c.saveData) return false;
  return !['slow-2g', '2g', '3g'].includes(c.effectiveType);
};

/** Fetches a file all the way through, so the whole of it is in the HTTP cache. */
const warm = async (url) => {
  if (!url) return;
  const res = await fetch(url, { mode: 'cors', credentials: 'omit' });
  if (!res.ok) throw new Error(`${res.status} for ${url}`);
  await res.blob();
};

/** Picks the next look and fetches it. One at a time; resolves when done or given up. */
function prepareNext() {
  if (preparing) return preparing;
  preparing = (async () => {
    const state = read();
    if (state.ready) return;
    if (navigator.onLine === false) return;

    const allowVideo = videoAllowed();
    const [{ data: templates }, { data: themes }] = await Promise.all([
      supabase
        .from('cover_image_templates')
        .select(TEMPLATE_POOL_COLUMNS)
        .eq('cover_type', 'invitation')
        .not('webp_url', 'is', null)
        .limit(TEMPLATE_POOL_LIMIT),
      allowVideo
        ? supabase.from('themes').select(THEME_POOL_COLUMNS).limit(THEME_POOL_LIMIT)
        : Promise.resolve({ data: [] }),
    ]);

    const look = choosePick({ templates, themes, avoid: [state.current], allowVideo });
    if (!look) return;

    await warm(look.template.webpUrl);
    if (look.video) {
      try {
        // The poster first: it is what shows while the video starts.
        await warm(look.video.thumbnailCdnUrl);
        await warm(look.video.videoCdnUrl);
      } catch (err) {
        // The template alone is still a look.
        console.warn('[coverPickAhead] video fetch failed:', err?.message ?? err);
        look.video = null;
      }
    }

    write({ ...read(), ready: look });
  })()
    .catch((err) => {
      console.warn('[coverPickAhead] could not prepare the next look:', err?.message ?? err);
    })
    .finally(() => { preparing = null; });
  return preparing;
}

/** When the site loads: fetch a look in the background if none is ready. */
export function startCoverPickAhead() {
  const run = () => { prepareNext(); };
  // After the page itself has loaded, when the browser is idle.
  if ('requestIdleCallback' in window) {
    const id = window.requestIdleCallback(run, { timeout: 5000 });
    return () => window.cancelIdleCallback(id);
  }
  const timer = setTimeout(run, 2000);
  return () => clearTimeout(timer);
}

/**
 * The look a new invitation opens with, or null when none has finished
 * fetching (the page then uses its bundled backgrounds). Taking it starts
 * fetching the next.
 */
export function takeCoverPick() {
  const state = read();
  // The same page asking twice (React mounts twice in development): the
  // same look, not a fallback.
  if (!state.ready && state.current && Date.now() - takenAt < 3000) return state.current;

  const taken = state.ready;
  if (taken) {
    write({ ready: null, current: taken });
    takenAt = Date.now();
  }
  setTimeout(() => { prepareNext(); }, NEXT_DELAY_MS);
  return taken;
}
